import React from 'react';
import PropTypes from 'prop-types';

/**
 * Test Results Component
 * Displays test results with score, feedback, and detailed breakdown
 */
const TestResults = ({ result, onRetake, onContinue }) => {
  const { evaluation } = result;

  if (!evaluation) {
    return (
      <div className="text-center p-8">
        <p className="text-gray-600">Результаты не найдены</p>
      </div>
    );
  }

  const { passed, percentage, totalScore, maxScore, results: questionResults } = evaluation;

  const getFeedbackMessage = () => {
    if (percentage >= 95) {
      return 'Превосходный результат! Вы продемонстрировали глубокие знания материала.';
    } else if (percentage >= 85) {
      return 'Отличная работа! Вы хорошо усвоили материал.';
    } else if (percentage >= 80) {
      return 'Хороший результат! Вы успешно прошли тест.';
    } else if (percentage >= 70) {
      return 'Неплохой результат, но есть над чем поработать. Рекомендуем пересдать тест.';
    } else {
      return 'К сожалению, тест не пройден. Повторите материал и попробуйте еще раз.';
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Result Header */}
      <div className={`rounded-lg shadow-lg p-8 mb-6 ${passed ? 'bg-gradient-to-r from-green-500 to-green-600' : 'bg-gradient-to-r from-red-500 to-red-600'}`}>
        <div className="text-center text-white">
          <div className="mb-4">
            {passed ? (
              <svg className="w-20 h-20 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg className="w-20 h-20 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
          </div>

          <h2 className="text-3xl font-bold mb-2">
            {passed ? 'Тест пройден!' : 'Тест не пройден'}
          </h2>

          <div className="text-6xl font-bold mb-4">
            {percentage}%
          </div>

          <p className="text-xl mb-2">
            {totalScore} из {maxScore} баллов
          </p>

          <p className="text-lg opacity-90">
            {getFeedbackMessage()}
          </p>
        </div>
      </div>

      {/* Detailed Results */}
      {questionResults && questionResults.length > 0 && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">
            Детальные результаты
          </h3>

          <div className="space-y-4">
            {questionResults.map((qResult, index) => (
              <div
                key={index}
                className={`border-l-4 p-4 rounded-lg ${
                  qResult.isCorrect
                    ? 'border-green-500 bg-green-50'
                    : 'border-red-500 bg-red-50'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center">
                    {qResult.isCorrect ? (
                      <svg className="w-6 h-6 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-6 h-6 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    )}
                    <span className="font-semibold text-gray-800">
                      Вопрос {index + 1}
                    </span>
                  </div>
                  <span className={`font-semibold ${qResult.isCorrect ? 'text-green-600' : 'text-red-600'}`}>
                    {qResult.earnedPoints} / {qResult.maxPoints} баллов
                  </span>
                </div>

                {!qResult.isCorrect && qResult.explanation && (
                  <div className="mt-2 p-3 bg-white rounded border border-gray-200">
                    <p className="text-sm font-semibold text-gray-700 mb-1">Пояснение:</p>
                    <p className="text-sm text-gray-600">{qResult.explanation}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Statistics */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4">Статистика</h3>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">
              {questionResults?.filter((r) => r.isCorrect).length || 0}
            </div>
            <div className="text-sm text-gray-600">Правильных ответов</div>
          </div>

          <div className="text-center p-4 bg-red-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">
              {questionResults?.filter((r) => !r.isCorrect).length || 0}
            </div>
            <div className="text-sm text-gray-600">Неправильных ответов</div>
          </div>

          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">
              {totalScore}
            </div>
            <div className="text-sm text-gray-600">Набрано баллов</div>
          </div>

          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {percentage}%
            </div>
            <div className="text-sm text-gray-600">Процент</div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        {!passed && onRetake && (
          <button
            onClick={onRetake}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 font-semibold"
          >
            Пересдать тест
          </button>
        )}

        {passed && onContinue && (
          <button
            onClick={onContinue}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 font-semibold"
          >
            Продолжить обучение
          </button>
        )}

        {!passed && (
          <button
            onClick={onContinue}
            className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold"
          >
            Вернуться к модулю
          </button>
        )}
      </div>
    </div>
  );
};

TestResults.propTypes = {
  result: PropTypes.shape({
    evaluation: PropTypes.shape({
      passed: PropTypes.bool.isRequired,
      percentage: PropTypes.number.isRequired,
      totalScore: PropTypes.number.isRequired,
      maxScore: PropTypes.number.isRequired,
      results: PropTypes.arrayOf(
        PropTypes.shape({
          questionId: PropTypes.number,
          isCorrect: PropTypes.bool,
          earnedPoints: PropTypes.number,
          maxPoints: PropTypes.number,
          explanation: PropTypes.string,
        })
      ),
    }).isRequired,
    attempt: PropTypes.object,
  }).isRequired,
  onRetake: PropTypes.func,
  onContinue: PropTypes.func,
};

export default TestResults;
